import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import { useTranslation } from '@blaze-cms/nextjs-components';
import FiltersList from './FiltersList';
import CloseMobileForm from './CloseMobileForm';
import ResetDesktopForm from './ResetDesktopForm';
import MobileFormToolbar from './MobileFormToolbar';
import { useFilterAggregationValues } from '../../../hooks';
import { isDeviceDesktop, getResponsiveFilterClassnames, buildUrlQuery } from '../helpers';
import { MORE_FILTERS_CLASSES } from '../constants';
import { TRANSLATION_NAMESPACE } from '../../../constants';

const SearchFilter = ({
  searchFilterRef,
  listComponentName,
  data: filterData,
  filters,
  hasUrl,
  entity,
  handleSearch,
  name,
  isCollapsedOnResponsive,
  displaySearchFilter,
  setDisplaySearchFilter,
  groupAfterDesktop,
  groupAfterMobile,
  filterValues,
  setAppliedFilters,
  mobileRefineButtonText,
  query
}) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const [moreFiltersMobileCollapsed, setMoreFiltersMobileCollapsed] = useState(true);
  const [moreFiltersDesktopCollapsed, setMoreFiltersDesktopCollapsed] = useState(true);

  const [data, setStateValues, setSelectProp] = useFilterAggregationValues({
    data: filterData,
    filterValues
  });

  const doSubmit = newValues => {
    const newQuery = filterValues
      ? buildUrlQuery(newValues, filters, listComponentName, query)
      : '';
    handleSearch(newQuery);
    setMoreFiltersDesktopCollapsed(true);
    setMoreFiltersMobileCollapsed(true);
  };

  const handleSubmit = debounce(newValues => {
    doSubmit(newValues);
  }, 200);

  useEffect(() => {
    if (filterValues.shouldSearch) {
      handleSubmit(filterValues);
      setAppliedFilters({ type: 'resetSearch' });
    }
  }, [filterValues, handleSubmit, setAppliedFilters]);

  const formClass = classnames('filter__form filter__form--initial', {
    'filter__form--mobile': isCollapsedOnResponsive && displaySearchFilter,
    'filter__form--collapsible': isCollapsedOnResponsive
  });

  const {
    moreFiltersMobileWrapperClass,
    moreFiltersMobileTogglerClass,
    moreFiltersDesktopWrapperClass,
    moreFiltersDesktopTogglerClass
  } = getResponsiveFilterClassnames(moreFiltersDesktopCollapsed, moreFiltersMobileCollapsed);

  const formId = `filter-${name}-form`;

  const handleReset = () => {
    setAppliedFilters({ type: 'reset' });
    doSubmit();
  };

  const updateFilterValues = (newValues, shouldSubmit) => {
    const [lastChangedFilter] = Object.keys(newValues);
    setSelectProp(lastChangedFilter);
    setAppliedFilters({ newValues, shouldSearch: shouldSubmit, type: 'update' });
  };

  const shouldGroup = !!(groupAfterDesktop || groupAfterMobile);

  return (
    <>
      <form
        ref={searchFilterRef}
        className={formClass}
        data-testid={formId}
        id={formId}
        onSubmit={e => {
          e.preventDefault();
          doSubmit(filterValues);
          setStateValues(filterData);
        }}>
        {displaySearchFilter && (
          <CloseMobileForm handleClose={() => setDisplaySearchFilter(false)} />
        )}

        <div className="filter filter--search-refine">
          <ResetDesktopForm handleReset={handleReset} />

          <div className="filter__wrapper filter__wrapper--search-refine">
            {!shouldGroup && (
              <FiltersList
                shouldSearch={!hasUrl}
                data={data}
                filters={filters}
                hasUrl={hasUrl}
                entity={entity}
                filterValues={filterValues}
                updateFilterValues={updateFilterValues}
              />
            )}

            {shouldGroup && (
              <>
                <FiltersList
                  shouldSearch={!hasUrl}
                  data={data}
                  filters={filters.slice(0, groupAfterMobile)}
                  hasUrl={hasUrl}
                  entity={entity}
                  filterValues={filterValues}
                  updateFilterValues={updateFilterValues}
                />

                {!!groupAfterMobile && (
                  <button
                    data-testId="search-filter-mobile-toggler"
                    className={moreFiltersMobileTogglerClass}
                    type="button"
                    onClick={() => setMoreFiltersMobileCollapsed(!moreFiltersMobileCollapsed)}>
                    Filters
                  </button>
                )}

                <div className={moreFiltersMobileWrapperClass}>
                  <div className={MORE_FILTERS_CLASSES.MOBILE_CONTENT}>
                    <FiltersList
                      shouldSearch={!hasUrl && isDeviceDesktop()}
                      data={data}
                      filters={filters.slice(
                        groupAfterMobile,
                        groupAfterDesktop ? groupAfterDesktop - 1 : 0
                      )}
                      hasUrl={hasUrl}
                      entity={entity}
                      filterValues={filterValues}
                      updateFilterValues={updateFilterValues}
                    />

                    {!!groupAfterDesktop && (
                      <button
                        data-testId="search-filter-desktop-toggler"
                        className={moreFiltersDesktopTogglerClass}
                        type="button"
                        aria-label={
                          moreFiltersDesktopCollapsed
                            ? 'Show more search filters'
                            : 'Show less search filters'
                        }
                        onClick={() =>
                          setMoreFiltersDesktopCollapsed(!moreFiltersDesktopCollapsed)
                        }>
                        {moreFiltersDesktopCollapsed ? 'More filters' : 'Less filters'}
                      </button>
                    )}

                    <div className={moreFiltersDesktopWrapperClass}>
                      <div className={MORE_FILTERS_CLASSES.DESKTOP_CONTENT}>
                        <FiltersList
                          shouldSearch={false}
                          data={data}
                          filters={filters.slice(groupAfterDesktop)}
                          hasUrl={hasUrl}
                          entity={entity}
                          filterValues={filterValues}
                          updateFilterValues={updateFilterValues}
                        />
                      </div>

                      <div className={MORE_FILTERS_CLASSES.DESKTOP_BUTTONS}>
                        <ResetDesktopForm handleReset={handleReset} />

                        <button className="button button--full-width" type="submit">
                          {t('SEARCH_BUTTON_TEXT')}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={MORE_FILTERS_CLASSES.MOBILE_BUTTONS}>
                    <ResetDesktopForm handleReset={handleReset} />

                    <button className="button button--full-width" type="submit">
                      {t('SEARCH_BUTTON_TEXT')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {!displaySearchFilter && (
              <button className="button button--full-width" type="submit">
                {t('SEARCH_BUTTON_TEXT')}
              </button>
            )}
          </div>
        </div>

        {displaySearchFilter && <MobileFormToolbar formId={formId} handleReset={handleReset} />}
      </form>

      {isCollapsedOnResponsive && !displaySearchFilter && (
        <div
          className="filter__refine filter__refine--mobile-close"
          data-testid="refine-mobile"
          aria-label={t('REFINE_SEARCH')}>
          <div role="button" onClick={() => setDisplaySearchFilter(true)}>
            {mobileRefineButtonText || t('REFINE_SEARCH')}
          </div>
        </div>
      )}
    </>
  );
};

SearchFilter.propTypes = {
  data: PropTypes.object.isRequired,
  filters: PropTypes.array.isRequired,
  searchFilterRef: PropTypes.object.isRequired,
  hasUrl: PropTypes.bool.isRequired,
  entity: PropTypes.string.isRequired,
  handleSearch: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  displaySearchFilter: PropTypes.bool.isRequired,
  setDisplaySearchFilter: PropTypes.func.isRequired,
  isCollapsedOnResponsive: PropTypes.bool.isRequired,
  groupAfterDesktop: PropTypes.number.isRequired,
  groupAfterMobile: PropTypes.number.isRequired,
  filterValues: PropTypes.object.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  listComponentName: PropTypes.string,
  query: PropTypes.object,
  mobileRefineButtonText: PropTypes.string
};

SearchFilter.defaultProps = {
  mobileRefineButtonText: '',
  listComponentName: '',
  query: {}
};

export default SearchFilter;
